import React, { useEffect, useMemo, useRef, useState } from 'react';
import useServiceUrls from '../../Utils/UseServiceUrls';
import {
    readCartSnippetParams,
    getFinalReturnUrl,
    buildAlertResumeRouteUrl,
    handleCartAlerts,
    completeAlertChain,
    getAlertChainDestinationUrl,
    onSnippetComplete,
    getAlertResumeGuardKey,
    mapServiceUrls,
} from '../../Helpers/Globals';
import { client } from '../../Utils/ApiClient';
import LoadingSpinner from '../LoadingSpinner';

const getGuardKeyFromParams = (params) => getAlertResumeGuardKey({
    sessionKey: params.sessionKey,
    cartUid: params.cartUid,
    cartExternalId: params.cartExternalId,
    cartSourceExternalId: params.cartSourceExternalId,
});

const isAlertChainComplete = (params) => {
    try {
        return !!sessionStorage.getItem(getGuardKeyFromParams(params));
    } catch {
        return false;
    }
};

function AlertResumeCompleteMessage() {
    return (
        <div className={'container'} style={{ padding: '16px', textAlign: 'center' }}>
            <h5>All alerts have been processed.</h5>
            <p style={{ marginTop: '8px' }}>
                Please close this window using your browser&apos;s close button (X) to continue.
            </p>
        </div>
    );
}

// How long to keep the spinner up after asking the window to close, before deciding it stayed open.
const CLOSE_MESSAGE_DELAY_MS = 1500;

export default function AlertResume() {
    const initialParams = useMemo(() => readCartSnippetParams(), []);
    const [sessionKey, setSessionKey] = useState(initialParams.sessionKey);
    const [loginDomain, setLoginDomain] = useState(initialParams.loginDomain);
    const [cartUid, setCartUid] = useState(initialParams.cartUid);
    const [cartExternalId, setCartExternalId] = useState(initialParams.cartExternalId);
    const [cartSourceExternalId, setCartSourceExternalId] = useState(initialParams.cartSourceExternalId);
    const [closeFailed, setCloseFailed] = useState(false);
    const { serviceUrls } = useServiceUrls(loginDomain);
    const startedRef = useRef(false);
    const closeTimerRef = useRef(null);

    const hasCart = useMemo(() => {
        return !!(cartUid || cartExternalId || cartSourceExternalId);
    }, [cartUid, cartExternalId, cartSourceExternalId]);

    const markComplete = (params = readCartSnippetParams()) => {
        try {
            sessionStorage.setItem(getGuardKeyFromParams(params), '1');
        } catch {
            // ignore session storage failures
        }
    };

    // Redirecting and closing both take the page away, so hold the spinner until one of them does.
    // Only a close that leaves the window open falls through to the manual-close message.
    const finishChain = (originSnippetUrl, { alreadyComplete = false } = {}) => {
        const destinationUrl = getAlertChainDestinationUrl(originSnippetUrl);
        console.log('[AlertResume] finishing chain', { destinationUrl, alreadyComplete });

        if (destinationUrl) {
            onSnippetComplete(destinationUrl);
            return;
        }

        if (alreadyComplete) {
            // This run already signalled bl_close on its way here; don't reload to signal it again.
            window?.close();
        } else {
            completeAlertChain(originSnippetUrl);
        }

        closeTimerRef.current = setTimeout(() => setCloseFailed(true), CLOSE_MESSAGE_DELAY_MS);
    };

    useEffect(() => {
        const params = readCartSnippetParams();
        setSessionKey(params.sessionKey);
        setLoginDomain(params.loginDomain);
        setCartUid(params.cartUid);
        setCartExternalId(params.cartExternalId);
        setCartSourceExternalId(params.cartSourceExternalId);
    }, [window.location.search]);

    useEffect(() => {
        const cancelCloseTimer = () => clearTimeout(closeTimerRef.current);
        // A close that navigates instead unloads this page; don't flash the message on the way out.
        window.addEventListener('pagehide', cancelCloseTimer);
        return () => {
            cancelCloseTimer();
            window.removeEventListener('pagehide', cancelCloseTimer);
        };
    }, []);

    const fetchCartData = async (urls) => {
        const uniqueParam = new Date().getTime();
        const cartQuery = `${cartUid ? `cartUid=${cartUid}&` : ''}${cartExternalId ? `externalId=${cartExternalId}&` : ''}${cartSourceExternalId ? `sourceExternalId=${cartSourceExternalId}&` : ''}`;
        const response = await client(urls.LoyaltyEngineApiUrl, `api/v4/${sessionKey}/carts?${cartQuery}_=${uniqueParam}`);
        return response?.data;
    };

    const refreshCartCustomer = async (urls, customer) => {
        await client(
            urls.LoyaltyEngineApiUrl,
            `api/v4/${sessionKey}/carts/commands/setCustomer?cartUid=${cartUid || null}&externalId=${cartExternalId || null}&sourceExternalId=${cartSourceExternalId || null}`,
            { body: { ...customer } }
        );
    };

    const processAlerts = async () => {
        if (!serviceUrls || !sessionKey || !hasCart) {
            return;
        }

        const params = readCartSnippetParams();
        const originSnippetUrl = getFinalReturnUrl(window.location.href);
        const urls = mapServiceUrls(serviceUrls);

        try {
            console.log('[AlertResume] processing alerts', {
                sessionKey,
                cartUid,
                originSnippetUrl,
            });

            let cartData = await fetchCartData(urls);
            const customer = cartData?.Cart?.Customer;
            if (customer?.Uid) {
                console.log('[AlertResume] refreshing loyalty via setCustomer');
                await refreshCartCustomer(urls, customer);
                cartData = await fetchCartData(urls);
            }

            const alertCount = cartData?.LoyaltySummary?.Alerts?.length ?? 0;
            console.log('[AlertResume] cart ready', {
                alertCount,
                alerts: cartData?.LoyaltySummary?.Alerts,
            });

            const resumeUrl = buildAlertResumeRouteUrl(window.location.href, originSnippetUrl);
            const redirected = handleCartAlerts(
                cartData,
                originSnippetUrl,
                () => {
                    markComplete(params);
                    finishChain(originSnippetUrl);
                },
                { resumeUrl }
            );
            if (!redirected) {
                markComplete(params);
            }
        } catch (error) {
            console.log('[AlertResume] failed', error);
            markComplete(params);
            finishChain(originSnippetUrl);
        }
    };

    useEffect(() => {
        if (startedRef.current || !sessionKey || !hasCart) {
            return;
        }

        // The guard means the chain is done; finish it without waiting on the service urls.
        if (isAlertChainComplete(readCartSnippetParams())) {
            startedRef.current = true;
            console.log('[AlertResume] chain already completed for this cart');
            finishChain(getFinalReturnUrl(window.location.href), { alreadyComplete: true });
            return;
        }

        if (!serviceUrls) {
            return;
        }
        startedRef.current = true;
        processAlerts();
    }, [serviceUrls, sessionKey, hasCart, cartUid, cartExternalId, cartSourceExternalId]);

    if (closeFailed) {
        return <AlertResumeCompleteMessage />;
    }

    return <LoadingSpinner />;
}
